import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Spinner, Alert, Badge, Button } from 'react-bootstrap';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { feedbackHrOneOnOneApiService } from '../../services/apiServices';
import { FeedbackApplicationResponse, FeedbackApplicationStatus } from '../../api-client';
import { getFeedbackApplicationStatusLabel } from '../utils/statusConverters';

const MyFeedbackHrOneOnOneDetailPage: React.FC = () => {
  const { applicationId } = useParams<{ applicationId: string }>();
  const navigate = useNavigate();

  const [application, setApplication] = useState<FeedbackApplicationResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!applicationId) {
      navigate('/feedback/hr-one-on-one/my');
      return;
    }

    const loadApplication = async () => {
      try {
        setLoading(true);
        const response = await feedbackHrOneOnOneApiService.getMyApplication(applicationId);
        setApplication(response.data || null);
      } catch (err: any) {
        console.error('Ошибка при загрузке моей заявки:', err);
        setError(err.message || 'Ошибка при загрузке заявки');
      } finally {
        setLoading(false);
      }
    };

    loadApplication();
  }, [applicationId, navigate]);

  const getStatusBadgeVariant = (status: FeedbackApplicationStatus) => {
    switch (status) {
      case FeedbackApplicationStatus.PENDING:
        return 'warning';
      case FeedbackApplicationStatus.SCHEDULED:
        return 'info';
      case FeedbackApplicationStatus.COMPLETED:
        return 'success';
      case FeedbackApplicationStatus.CANCELED:
        return 'danger';
      default:
        return 'secondary';
    }
  };

  if (loading) {
    return (
      <Container
        fluid
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: '50vh' }}
      >
        <div className="text-center">
          <Spinner animation="border" />
          <p className="mt-2">Загрузка заявки...</p>
        </div>
      </Container>
    );
  }

  if (error || !application) {
    return (
      <Container fluid>
        <Row className="mt-4">
          <Col>
            <Alert variant="danger">{error || 'Заявка не найдена'}</Alert>
            <Button variant="primary" onClick={() => navigate('/feedback/hr-one-on-one/my')}>
              Вернуться к списку
            </Button>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Container fluid>
      <Row className="mt-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2>Заявка №{application.id}</h2>
            <Link to="/feedback/hr-one-on-one/my">
              <Button variant="outline-secondary">Вернуться к списку</Button>
            </Link>
          </div>

          <Card>
            <Card.Header>
              <Badge bg={getStatusBadgeVariant(application.status)}>
                {getFeedbackApplicationStatusLabel(application.status)}
              </Badge>
            </Card.Header>
            <Card.Body>
              <table className="table table-bordered">
                <tbody>
                  <tr>
                    <th style={{ width: '30%' }}>ID заявки</th>
                    <td>{application.id}</td>
                  </tr>
                  <tr>
                    <th>Тип</th>
                    <td>
                      <Badge bg="primary">HR 1-on-1</Badge>
                    </td>
                  </tr>
                  <tr>
                    <th>Статус</th>
                    <td>
                      <Badge bg={getStatusBadgeVariant(application.status)}>
                        {getFeedbackApplicationStatusLabel(application.status)}
                      </Badge>
                    </td>
                  </tr>
                  <tr>
                    <th>Дата создания</th>
                    <td>{new Date(application.created_at).toLocaleString()}</td>
                  </tr>
                  <tr>
                    <th>Описание</th>
                    <td>{application.description || 'Описание отсутствует'}</td>
                  </tr>
                </tbody>
              </table>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default MyFeedbackHrOneOnOneDetailPage;
